import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { pushEscapeLayer } from './escape-stack';
import { useModalFocusTrap } from './useModalFocusTrap';

type ModalProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  // Optional gate for close attempts (backdrop/esc/close-button).
  // Return false (or Promise<false>) to block closing.
  onRequestClose?: (reason: 'backdrop' | 'esc' | 'close-button') => boolean | Promise<boolean>;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  // New options
  disableBackdropClose?: boolean;
  disableEscClose?: boolean;
  closeOnBackdrop?: boolean; // default true (unless disableBackdropClose)
  closeOnEsc?: boolean; // default true (unless disableEscClose)
  trapFocus?: boolean; // default true
  returnFocusOnClose?: boolean; // default true
  // Provide a custom header (replaces default title/close row)
  header?: React.ReactNode;
  // Accessible name for the dialog (the default header's title is a heading, not a label).
  ariaLabel?: string;
  // Hide the default header close button when using the default header
  hideCloseButton?: boolean;
  // Style customizations (global polished defaults with escape hatch)
  containerClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  // Switch to legacy spacing/tone if a caller needs it
  variant?: 'polished' | 'legacy';
};

export function Modal({
  open,
  title,
  onClose,
  onRequestClose,
  children,
  footer,
  className = '',
  disableBackdropClose = false,
  disableEscClose = false,
  closeOnBackdrop,
  closeOnEsc,
  trapFocus = true,
  returnFocusOnClose = true,
  header,
  ariaLabel,
  hideCloseButton = false,
  containerClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  variant = 'polished',
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const allowBackdrop = (closeOnBackdrop ?? true) && !disableBackdropClose;
  const allowEsc = (closeOnEsc ?? true) && !disableEscClose;

  const handleRequestClose = useCallback((reason: 'backdrop' | 'esc' | 'close-button') => {
    if ((reason === 'backdrop' && !allowBackdrop) || (reason === 'esc' && !allowEsc)) {
      return;
    }
    if (typeof onRequestClose === 'function') {
      const result = onRequestClose(reason);
      if (result !== null && typeof result === 'object' && typeof (result as any).then === 'function') {
        void (result).then((v) => { if (v !== false) onClose(); });
        return;
      }
      if (result === false) return;
    }
    onClose();
  }, [allowBackdrop, allowEsc, onRequestClose, onClose]);

  useEffect(() => {
    if (!open || !allowEsc) return;
    return pushEscapeLayer(() => handleRequestClose('esc'));
  }, [open, allowEsc, handleRequestClose]);

  useModalFocusTrap({ open, containerRef, trapFocus, returnFocusOnClose });

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/30" data-testid="modal-backdrop" onClick={allowBackdrop ? () => handleRequestClose('backdrop') : undefined} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        ref={containerRef}
        data-testid="modal-dialog"
        className={`relative z-10 bg-bg-raised text-content-primary shadow-xl ${
        // Full-screen on mobile; on desktop clearly narrower than the 6xl layout to reveal context behind
        'w-full h-full rounded-none border-0 md:w-full md:h-auto md:max-h-[calc(100vh-96px)] md:rounded-lg md:border md:max-w-[min(100vw-96px,67rem)]'
      } overflow-hidden flex flex-col ${className} ${containerClassName || ''}`}>
        {header ? (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3'} border-b border-border-secondary flex items-center justify-between ${headerClassName || ''}`}>
            {header}
          </div>
        ) : (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3'} border-b border-border-secondary flex items-center justify-between ${headerClassName || ''}`}>
            <h3 className="text-lg font-semibold">{title}</h3>
            {!hideCloseButton && (
              <button className="px-2 py-1 rounded border border-border-primary hover:bg-bg-secondary" onClick={() => handleRequestClose('close-button')} aria-label="Close" data-testid="modal-close">✕</button>
            )}
          </div>
        )}
        <div data-modal-body-scroll className={`flex-1 overflow-auto ${variant === 'legacy' ? 'p-4' : 'p-4'} ${bodyClassName || ''}`}>
          {children}
        </div>
        {footer && (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3 bg-bg-secondary'} border-t border-border-secondary flex items-center justify-end gap-2 ${footerClassName || ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

